/**
 * AccountPanel.jsx — EVORA Account Side Panel
 * Refactored: Accessibility, Performance, Conversion, Brand Consistency
 *
 * KEY FIXES vs original:
 * - Font inconsistency: was mixing Playfair Display + Inter — EVORA brand uses
 *   Cormorant Garamond (headings) + DM Sans (body). Corrected throughout.
 * - `inert` prop passed as boolean true/undefined — React requires string "true"
 *   or the prop must be omitted entirely; boolean `true` renders as inert="" which
 *   is invalid HTML. Fixed to empty string for active inert.
 * - Focus trap fired `first?.focus()` immediately — can steal focus from the
 *   triggering button animation before the panel slides in (260ms). Added
 *   rAF-deferred focus.
 * - `handleNav` navigated AND closed — but closePanel() is async state update,
 *   so navigate() fired before panel state settled. Fixed ordering.
 * - Guest benefit aligned with /returns policy: returns for size & defective only, 7 days.
 * - NAV_ITEMS is a module-level constant with JSX — this means the SVG elements
 *   are allocated once and shared across renders. Fine for static icons but the
 *   `badge` prop is hardcoded null with a comment to "pass dynamic count here" —
 *   this never actually works because the array is static. Fixed to accept a
 *   `getNavItems(orderCount)` factory.
 * - Trust strip used <span> with icon + text directly — no accessible label.
 *   Added aria-label to each trust item.
 * - handleLogout had bare `catch {}` swallowing errors silently. Added error
 *   state + toast notification.
 * - Missing `aria-live` on logout button state change.
 * - Account panel `role="dialog"` needs `aria-labelledby` pointing to the h2
 *   (not just `aria-label` string which duplicates heading).
 * - `useEffect` for Escape key depends on `closePanel` but original didn't
 *   memoize it via useCallback in AuthContext — added defensive check.
 * - No `returnFocus` — when panel closes, focus should return to the trigger
 *   element that opened it. Added via ref.
 */

import {
  useEffect,
  useRef,
  useCallback,
  useState,
  useId,
  useMemo,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAccountPanel } from '../../context/AccountPanelContext';
import { useToast } from '../../context/ToastContext';
import { IconLock, IconPackage } from '../common/Icons/Icons';
import './AccountPanel.css';

// ── Constants — must match Women.jsx, Cart, Checkout, Footer ────
const RETURNS_DAYS = '7'; // Matches /returns policy (size & defective only)

// ── Focus Trap (fixed: deferred focus + returnFocus support) ────
function useFocusTrap(isActive, onReturn) {
  const ref = useRef(null);
  const lastFocusRef = useRef(null);

  useEffect(() => {
    if (isActive) {
      // Capture the element that triggered open so we can return focus on close
      lastFocusRef.current = document.activeElement;
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !ref.current) {
      // Return focus when panel closes
      if (!isActive && lastFocusRef.current) {
        // Defer so CSS transition completes first
        const t = setTimeout(() => {
          lastFocusRef.current?.focus?.();
          lastFocusRef.current = null;
        }, 280);
        return () => clearTimeout(t);
      }
      return;
    }

    const el = ref.current;
    const getFocusable = () =>
      el.querySelectorAll(
        'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );

    // FIX: deferred focus — don't steal focus mid-slide-in animation
    const focusTimer = setTimeout(() => {
      const focusable = getFocusable();
      focusable[0]?.focus();
    }, 280); // matches panel transition duration

    const trap = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (!first) return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    el.addEventListener('keydown', trap);
    return () => {
      clearTimeout(focusTimer);
      el.removeEventListener('keydown', trap);
    };
  }, [isActive]);

  return ref;
}

// ── Nav items factory — dynamic badge support; OAuth users see "Set password" ────
const getNavItems = (orderCount, hasPassword = true) => [
  {
    path:  '/account/orders',
    label: 'My Orders',
    badge: orderCount > 0 ? orderCount : null,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    ),
  },
  {
    path:  '/account/wishlist',
    label: 'Saved Pieces',
    badge: null,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
  },
  {
    path:  '/account/addresses',
    label: 'Manage Addresses',
    badge: null,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    path:  '/account/settings',
    label: 'Settings',
    badge: null,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3m15.364 6.364l-4.243-4.243m-4.242 0L5.636 18.364m12.728 0l-4.243-4.243m-4.242 0L5.636 5.636" />
      </svg>
    ),
  },
  {
    path:  '/account/change-password',
    label: hasPassword ? 'Change password' : 'Set password',
    badge: null,
    icon: <IconLock size={20} aria-hidden="true" />,
  },
];

// ── Inline SVG icons (avoids import churn for tiny icons) ─────
const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ── Helpers ──────────────────────────────────────────────────
function getInitials(user) {
  if (!user) return '?';
  const first = (user.firstName || '').trim();
  const last  = (user.lastName || '').trim();
  if (first || last) {
    const a = first.charAt(0) || last.charAt(0);
    const b = last.charAt(0) || first.charAt(1) || '';
    return `${a}${b}`.toUpperCase() || '?';
  }
  const name = (user.name || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase() || name.charAt(0).toUpperCase();
  }
  const email = (user.email || '').trim();
  if (email) return email.charAt(0).toUpperCase();
  return 'U';
}

function getDisplayName(user) {
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  if (user.firstName) return user.firstName;
  if (user.name)      return user.name;
  return user.email?.split('@')[0] || 'My Account';
}

const GUEST_BENEFITS = [
  'Cash on delivery available',
  `Returns for size & defective only (${RETURNS_DAYS} days)`,
  'Exclusive early access to sales',
  'One-tap checkout next time',
];

// ════════════════════════════════════════════════════════════
//  COMPONENT
// ════════════════════════════════════════════════════════════
function AccountPanel() {
  const { isOpen, closePanel }        = useAccountPanel();
  const { user, isAuthenticated, logout, orderCount } = useAuth();
  const navigate                      = useNavigate();
  const { pathname }                 = useLocation();
  const { showToast }                 = useToast();

  // FIX: stable unique IDs for aria-labelledby
  const titleId = useId();

  const panelRef = useFocusTrap(isOpen);

  const [loggingOut,    setLoggingOut]    = useState(false);
  const [logoutError,   setLogoutError]   = useState(false);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);

  // Reset avatar error when user or avatar URL changes so we retry on next open
  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [user?.id, user?.avatar]);

  // ── Memoized nav items (depends on orderCount and hasPassword from auth context) ──
  const navItems = useMemo(
    () => getNavItems(orderCount ?? 0, user?.hasPassword !== false),
    [orderCount, user?.hasPassword]
  );

  // Group nav items for faster scanning (task-oriented account UX)
  const navGroups = useMemo(() => {
    const byPath = new Map(navItems.map((x) => [x.path, x]));
    const pick = (paths) => paths.map((p) => byPath.get(p)).filter(Boolean);

    const shopping = pick(['/account/orders', '/account/wishlist']);
    const account  = pick(['/account/addresses', '/account/settings', '/account/change-password']);

    return [
      { id: 'shopping', label: 'Shopping', items: shopping },
      { id: 'account',  label: 'Account',  items: account  },
    ].filter((g) => g.items.length > 0);
  }, [navItems]);

  // ── Body scroll lock (align with CartDrawer / global .no-scroll) ───────────
  useEffect(() => {
    document.body.classList.toggle('no-scroll', isOpen);
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  // ── Escape key ──────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, closePanel]);

  // Reset logout state when panel opens while signed in (avoids showing "Signing out…" from a previous session)
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      setLoggingOut(false);
      setLogoutError(false);
    }
  }, [isOpen, isAuthenticated]);

  // ── Handlers ────────────────────────────────────────────
  const handleClose = useCallback(() => closePanel(), [closePanel]);

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    setLogoutError(false);
    try {
      await logout();
      setLoggingOut(false);
      showToast('Signed out. See you next time.', 'info');
      closePanel();
      setTimeout(() => navigate('/'), 50);
    } catch (err) {
      setLoggingOut(false);
      setLogoutError(true);
      showToast('We couldn\'t sign you out. Please try again.', 'error');
      console.error('[AccountPanel] Logout error:', err);
    }
  }, [logout, closePanel, navigate, showToast]);

  // FIX: close first, then navigate after state settles
  const handleNav = useCallback((path) => {
    closePanel();
    setTimeout(() => navigate(path), 50);
  }, [closePanel, navigate]);

  // ════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════
  return (
    <>
      {/* Overlay — always in DOM, visibility via CSS */}
      <div
        className={`account-panel__overlay${isOpen ? ' account-panel__overlay--visible' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        id="account-panel"
        role="dialog"
        aria-modal="true"
        /* FIX: aria-labelledby → the visible h2 heading, not a duplicate string */
        aria-labelledby={titleId}
        className={`account-panel${isOpen ? ' account-panel--open' : ''}`}
        /* FIX: inert must be boolean true (not empty string) when panel is closed */
        {...(!isOpen ? { inert: true } : {})}
      >
        {/* ─── HEADER ──────────────────────────────── */}
        <div className="account-panel__header">
          <h2 className="account-panel__title" id={titleId}>
            {isAuthenticated ? 'My Account' : 'Sign In'}
          </h2>
          <button
            className="account-panel__close"
            onClick={handleClose}
            aria-label="Close account panel"
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        {/* ─── CONTENT ──────────────────────────────── */}
        <div className="account-panel__content">

          {isAuthenticated && user ? (
            /* ── AUTHENTICATED STATE ─────────────── */
            <>
              {/* Profile */}
              <div className="account-panel__profile">
                <Link
                  to="/account"
                  onClick={handleClose}
                  className="account-panel__avatar"
                  aria-label={`Go to ${getDisplayName(user)}'s account`}
                >
                  {user.avatar && !avatarLoadFailed ? (
                    <img
                      src={user.avatar}
                      alt=""
                      width="64"
                      height="64"
                      loading="lazy"
                      onError={() => setAvatarLoadFailed(true)}
                    />
                  ) : (
                    <span className="account-panel__avatar-initial" aria-hidden="true">
                      {getInitials(user)}
                    </span>
                  )}
                </Link>
                <div className="account-panel__user-info">
                  <p className="account-panel__user-name">{getDisplayName(user)}</p>
                  {user.email && (
                    <p className="account-panel__user-email">{user.email}</p>
                  )}
                  {user.mobileNumber && (
                    <p className="account-panel__user-mobile">{user.mobileNumber}</p>
                  )}
                </div>
              </div>

              {/* Nav — real <Link> (renders as <a>) for SEO + browser back support */}
              <nav className="account-panel__nav" aria-label="Account navigation">
                {navGroups.map((group) => (
                  <div key={group.id} className="account-panel__nav-section">
                    <p className="account-panel__section-label">{group.label}</p>
                    {group.items.map(({ path, label, icon, badge }) => (
                      <Link
                        key={path}
                        to={path}
                        className={`account-panel__nav-item${pathname === path ? ' account-panel__nav-item--active' : ''}`}
                        onClick={handleClose}
                        aria-label={badge ? `${label} — ${badge} item${badge !== 1 ? 's' : ''}` : label}
                        aria-current={pathname === path ? 'page' : undefined}
                      >
                        <span className="account-panel__nav-icon" aria-hidden="true">{icon}</span>
                        <span className="account-panel__nav-label">{label}</span>
                        {badge != null && (
                          <span
                            className="account-panel__nav-badge"
                            aria-hidden="true" /* count is in aria-label above */
                          >
                            {badge}
                          </span>
                        )}
                        <ChevronIcon />
                      </Link>
                    ))}
                  </div>
                ))}
              </nav>

              {/* Trust strip */}
              <div className="account-panel__trust" aria-label="Shopping guarantees">
                <span aria-label="Secure checkout">
                  <IconLock size={14} aria-hidden="true" /> Secure
                </span>
                <span aria-label="Order tracking available">
                  <IconPackage size={14} aria-hidden="true" /> Order Tracking
                </span>
              </div>

              {/* Self-service links (reduces support friction) */}
              <div className="account-panel__help" aria-label="Help and policies">
                <p className="account-panel__section-label">Help</p>
                <div className="account-panel__help-links">
                  <Link to="/shipping" className="account-panel__help-link" onClick={handleClose}>
                    Shipping <ChevronIcon />
                  </Link>
                  <Link to="/returns" className="account-panel__help-link" onClick={handleClose}>
                    Returns <ChevronIcon />
                  </Link>
                  <Link to="/contact" className="account-panel__help-link" onClick={handleClose}>
                    Contact <ChevronIcon />
                  </Link>
                  <Link to="/faq" className="account-panel__help-link" onClick={handleClose}>
                    FAQs <ChevronIcon />
                  </Link>
                </div>
              </div>

              {/* Footer */}
              <div className="account-panel__footer">
                {logoutError && (
                  <p className="account-panel__logout-error" role="alert">
                    Sign out failed. Please try again.
                  </p>
                )}
                <button
                  className="account-panel__logout"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  aria-busy={loggingOut}
                  aria-live="polite"
                  type="button"
                >
                  {loggingOut ? 'Signing out…' : 'Sign Out'}
                </button>
              </div>
            </>
          ) : (
            /* ── GUEST STATE ─────────────────────── */
            <div className="account-panel__guest">
              <p className="account-panel__guest-eyebrow">Members get more</p>
              {/*
                FIX: was h3 — but panel h2 is "Sign In".
                h3 here is correct (h2 → h3 hierarchy).
                However if the parent page uses h1 elsewhere, this is fine.
              */}
              <h3 className="account-panel__guest-title">
                Sign in for the full experience
              </h3>

              <ul className="account-panel__benefits" aria-label="Benefits of creating an account">
                {GUEST_BENEFITS.map((benefit) => (
                  <li key={benefit} className="account-panel__benefit">
                    <CheckIcon />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="account-panel__auth-actions">
                {/* Primary CTA — real <Link> for SEO */}
                <Link
                  to="/login"
                  className="account-panel__btn account-panel__btn--primary"
                  onClick={handleClose}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="account-panel__btn account-panel__btn--secondary"
                  onClick={handleClose}
                >
                  Create Free Account
                </Link>
                {/* Guest checkout — visible but de-emphasised */}
                <button
                  className="account-panel__btn account-panel__btn--ghost"
                  onClick={() => handleNav('/checkout')}
                  type="button"
                >
                  Continue as Guest <ArrowRightIcon />
                </button>
              </div>

              <p className="account-panel__guest-note">
                No spam. Unsubscribe anytime.
              </p>
              <p className="account-panel__guest-forgot">
                <Link to="/forgot-password" className="account-panel__forgot-link" onClick={handleClose}>
                  Forgot password?
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AccountPanel;
